
import { Vue, Component, Watch } from "vue-property-decorator";
import dayjs from "dayjs";
import { Action, Getter, Mutation } from "vuex-class";
import {
  OverlayAction,
  OverlayGetter,
  OverlayMutation,
  OverlaySection
} from "@/components/overlay/types";
import { Namespace } from "@/store/types";
import {
  EpgItem,
  Episode,
  Movie,
  Season
} from "@/services/apiService/api.types";
import EpgIcon from "@/assets/svg/svg-home/09-epg.vue";
import { isEpisode } from "@/helpers/utils";
import { EventBus } from "@/main";
import { PlayerControlsEnum } from "@/helpers/constants";
import { PlayerAction } from "@/components/player/types";
import { LocaleOverlay } from "@/plugins/i18n/types";

@Component({
  components: {
    EpgIcon
  }
})
export default class ContentNext extends Vue {
  @Action(OverlayAction.UpdateOverlayOpen, { namespace: Namespace.Overlay })
  updateOverlayStatus!: (overlaySectionStatus: OverlaySection) => void;

  @Action(PlayerAction.UpdatePause, { namespace: Namespace.Player })
  updatePause!: (pause: boolean) => void;

  @Action(OverlayAction.UpdateActiveVodItemProgressDuration, {
    namespace: Namespace.Overlay
  })
  updateActiveVodItemProgressDuration!: (
    playbackTimestamp: number | null
  ) => void;

  @Mutation(OverlayMutation.SetActiveVodItem, {
    namespace: Namespace.Overlay
  })
  setActiveVodItem!: (vod: Movie | Episode | null) => void;

  @Getter(OverlayGetter.GetActiveEpgItem, {
    namespace: Namespace.Overlay
  })
  getActiveEpgItem!: EpgItem;

  @Getter(OverlayGetter.GetChannelEpg, {
    namespace: Namespace.Overlay
  })
  getChannelEpg!: EpgItem[];

  @Getter(OverlayGetter.GetOverlayOpen, {
    namespace: Namespace.Overlay
  })
  getOverlayOpen!: OverlaySection;

  @Getter(OverlayGetter.GetTvShowMeta, {
    namespace: Namespace.Overlay
  })
  getTvShowMeta!: Season[] | null;

  @Getter(OverlayGetter.GetActiveVodItem, {
    namespace: Namespace.Overlay
  })
  getActiveVodItem!: Movie | Episode | null;

  @Getter(OverlayGetter.GetActiveVodItemProgressDuration, {
    namespace: Namespace.Overlay
  })
  getActiveVodItemProgressDuration!: number | null;

  @Getter(OverlayGetter.GetActiveVodItemDuration, {
    namespace: Namespace.Overlay
  })
  getActiveVodItemDuration!: number | null;

  nextContentItem: EpgItem | Episode | null = null;

  nextEpisodeSeason = "";

  nextEpisodeTimeout = 0;

  LocaleOverlay = LocaleOverlay;

  mounted(): void {
    this.nextContentItem = this.contentNextInfo();
  }

  get epgNextStart(): string {
    if (!this.nextContentItem || isEpisode(this.nextContentItem)) {
      return "--";
    }
    return dayjs(this.nextContentItem.epgStarttime).format("HH:mm");
  }

  get epgNextEnd(): string {
    if (!this.nextContentItem || isEpisode(this.nextContentItem)) {
      return "--";
    }
    return dayjs(this.nextContentItem.epgEndtime).format("HH:mm");
  }

  get episodeDuration(): string {
    if (!this.nextContentItem || !isEpisode(this.nextContentItem)) {
      return "--";
    }
    return "duration" in this.nextContentItem
      ? this.nextContentItem.duration!
      : "";
  }

  get nextEpisodeName(): string {
    if (!this.nextContentItem || !isEpisode(this.nextContentItem)) {
      return "--";
    }
    return this.nextContentItem.name;
  }

  get playbackEnd(): boolean {
    if (this.getActiveVodItemDuration && this.getActiveVodItemProgressDuration)
      return (
        this.getActiveVodItemDuration === this.getActiveVodItemProgressDuration
      );

    return false;
  }

  contentNextInfo(): EpgItem | Episode | null {
    //this component only renders on Tv shows and channels and not movies so this condition is optimal
    if (this.getTvShowMeta && isEpisode(this.getActiveVodItem)) {
      for (let i = 0; i < this.getTvShowMeta!.length; i++) {
        for (let j = 0; j < this.getTvShowMeta![i].episodes.length; j++) {
          if (
            this.getTvShowMeta![i].episodes[j].id === this.getActiveVodItem.id
          ) {
            if (j < this.getTvShowMeta![i].episodes.length - 1) {
              this.nextEpisodeSeason = this.getTvShowMeta[i].name;
              return this.getTvShowMeta![i].episodes[j + 1];
            } else if (i < this.getTvShowMeta!.length - 1) {
              if (this.getTvShowMeta[i + 1].episodes.length > 0) {
                this.nextEpisodeSeason = this.getTvShowMeta[i + 1].name;
                return this.getTvShowMeta![i + 1].episodes[0];
              }
              return null;
            } else {
              return null;
            }
          }
        }
      }
    } else if (
      this.getActiveEpgItem.id !==
      this.getChannelEpg[this.getChannelEpg.length - 1].id
    ) {
      for (let i = this.getChannelEpg.length - 1; i > 0; i--) {
        if (this.getChannelEpg[i].id === this.getActiveEpgItem.id) {
          return this.getChannelEpg[i + 1];
        }
      }
    }
    return null;
  }

  openContentMenu(): void {
    this.updateOverlayStatus("ContentList");
  }

  playNext(): void {
    if (this.getTvShowMeta && isEpisode(this.contentNextInfo())) {
      this.setActiveVodItem(this.contentNextInfo() as Episode);
      EventBus.$emit(PlayerControlsEnum.Start);
      this.updatePause(false);
      this.updateActiveVodItemProgressDuration(null);
    }
  }

  @Watch("getActiveEpgItem", { deep: true })
  handleEpgItemChange(): void {
    this.nextContentItem = this.contentNextInfo();
  }

  @Watch("playbackEnd")
  handleNextEpisode(newVal: boolean): void {
    if (newVal) {
      this.nextEpisodeTimeout = setTimeout(() => {
        this.playNext();
      }, 5000);
    }

    if (!newVal) {
      clearTimeout(this.nextEpisodeTimeout);
    }
  }
}
